import {CommonModule} from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  ViewChild
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {
  FloatingChatHistoryResponse,
  FloatingChatMessage,
  FloatingChatService
} from './floating-chat.service';
import {
  createFloatingChatStorage,
  getFloatingChatRuntimeConfig,
  resolveFloatingChatRgbChannels
} from './floating-chat.runtime';

@Component({
  selector: 'app-floating-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './floating-chat.component.html',
  styleUrl: './floating-chat.component.scss'
})
export class FloatingChatComponent implements AfterViewChecked {
  private readonly runtimeConfig = getFloatingChatRuntimeConfig();
  private readonly chatService = inject(FloatingChatService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly oidcSecurityService = inject(OidcSecurityService, {optional: true});
  private readonly storage = createFloatingChatStorage();
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @HostBinding('style.--floating-chat-primary')
  readonly floatingChatPrimary = this.runtimeConfig.primaryColor ?? 'var(--primary-color)';

  @HostBinding('style.--floating-chat-primary-rgb')
  readonly floatingChatPrimaryRgb = this.runtimeConfig.primaryColorRgb
    ?? (this.runtimeConfig.primaryColor
      ? resolveFloatingChatRgbChannels(this.runtimeConfig.primaryColor)
      : 'var(--primary-rgb)');

  @HostBinding('style.--floating-chat-font-family')
  readonly floatingChatFontFamily = this.runtimeConfig.fontFamily ?? "'IBM Plex Sans Thai', sans-serif";

  @HostBinding('class.chat-dismissed')
  get chatDismissed(): boolean {
    return this.isDismissed;
  }

  @ViewChild('historyBox') private historyBox?: ElementRef<HTMLDivElement>;
  @ViewChild('draftInput') private draftInput?: ElementRef<HTMLTextAreaElement>;

  isVisible = false;
  isDismissed = false;
  isAuthenticated = false;
  isSending = false;
  pendingScroll = false;
  isShaking = false;

  readonly particles = [0, 1, 2, 3, 4, 5, 6, 7];

  private shakeInterval?: ReturnType<typeof setInterval>;
  private headerObserver?: ResizeObserver;
  private shakeTimeout?: ReturnType<typeof setTimeout>;

  readonly visitorAvatar = this.runtimeConfig.visitorAvatar ?? "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='32' fill='%23dbeafe'/%3E%3Ccircle cx='32' cy='24' r='12' fill='%231d4ed8'/%3E%3Cpath d='M14 54c4-10 14-16 18-16s14 6 18 16' fill='%231d4ed8'/%3E%3C/svg%3E";
  readonly systemAvatar = this.runtimeConfig.systemAvatar ?? 'images/logo2.png';
  readonly systemName = this.runtimeConfig.systemName ?? 'มกอช.';

  name = '';
  tel = '';
  draft = '';
  messages: FloatingChatMessage[] = [];

  private get sessionStorageKey(): string {
    return `${this.runtimeConfig.storagePrefix ?? 'floating-chat'}-session-id`;
  }

  private get nameStorageKey(): string {
    return `${this.runtimeConfig.storagePrefix ?? 'floating-chat'}-name`;
  }

  private get telStorageKey(): string {
    return `${this.runtimeConfig.storagePrefix ?? 'floating-chat'}-tel`;
  }

  readonly sessionId = this.ensureSessionId();

  constructor() {
    this.name = this.storage.getItem(this.nameStorageKey) ?? '';
    this.tel = this.storage.getItem(this.telStorageKey) ?? '';
    if (this.oidcSecurityService) {
      this.oidcSecurityService.isAuthenticated$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(({isAuthenticated}) => {
          this.isAuthenticated = isAuthenticated;
          this.requestRender();
        });
      this.oidcSecurityService.userData$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(({userData}) => {
          if (!userData) {
            return;
          }
          const resolvedName = userData.name || userData.given_name || this.name;
          if (resolvedName && resolvedName !== this.name) {
            this.name = resolvedName;
          }
          if (this.name || this.tel) {
            this.persistIdentity();
          }
          this.requestRender();
        });
    }
    this.loadHistory();
    this.startShakeInterval();
    this.destroyRef.onDestroy(() => this.stopTrackingPanelTop());
  }

  ngAfterViewChecked(): void {
    if (!this.pendingScroll || !this.historyBox) {
      return;
    }
    this.historyBox.nativeElement.scrollTop = this.historyBox.nativeElement.scrollHeight;
    this.pendingScroll = false;
  }

  get hasIdentity(): boolean {
    if (this.isAuthenticated) {
      return true;
    }
    return this.name.trim().length > 0;
  }

  get requiresIdentity(): boolean {
    return !this.isAuthenticated && !this.name.trim();
  }

  toggleChat(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.isVisible = !this.isVisible;
    this.pendingScroll = this.isVisible;
    if (this.isVisible) {
      this.trackPanelTop();
      this.focusDraft();
    } else {
      this.stopTrackingPanelTop();
    }
  }

  minimizeChat(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.isVisible = false;
    this.isDismissed = false;
    this.isShaking = false;
    this.pendingScroll = false;
    this.stopTrackingPanelTop();
  }

  closeChat(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isVisible = false;
    this.isDismissed = true;
    this.stopTrackingPanelTop();
  }

  confirmIdentity(): void {
    if (!this.hasIdentity) {
      return;
    }
    this.persistIdentity();
    this.pendingScroll = true;
    this.focusDraft();
  }

  sendMessage(): void {
    const trimmed = this.draft.trim();
    if (!trimmed || this.isSending || !this.hasIdentity) {
      return;
    }

    const now = new Date().toISOString();
    this.messages = [...this.messages, {user: '1', message: trimmed, time: now}];
    this.draft = '';
    this.isSending = true;
    this.pendingScroll = true;
    this.persistIdentity();

    this.chatService.sendMessage({
      sessionId: this.sessionId,
      name: this.name.trim(),
      tel: this.tel.trim(),
      message: trimmed
    }).subscribe({
      next: (response) => {
        this.messages = [...this.messages, {user: '2', message: response.message, time: new Date().toISOString()}];
        this.isSending = false;
        this.pendingScroll = true;
        this.requestRender();
        this.focusDraft();
      },
      error: () => {
        this.messages = [...this.messages, {
          user: '2',
          message: 'ไม่สามารถส่งข้อความได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง',
          time: new Date().toISOString()
        }];
        this.isSending = false;
        this.pendingScroll = true;
        this.requestRender();
        this.focusDraft();
      }
    });
  }

  onDraftKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }
    event.preventDefault();
    this.sendMessage();
  }

  trackMessage(index: number, message: FloatingChatMessage): string {
    return `${message.user}-${message.time}-${index}`;
  }

  displayName(message: FloatingChatMessage): string {
    return message.user === '1' ? this.name || (this.isAuthenticated ? 'ผู้ใช้งาน' : 'ผู้เยี่ยมชม') : this.systemName;
  }

  avatar(message: FloatingChatMessage): string {
    return message.user === '1' ? this.visitorAvatar : this.systemAvatar;
  }

  formatTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleTimeString('th-TH', {hour: '2-digit', minute: '2-digit'});
  }

  isVisitorMessage(message: FloatingChatMessage): boolean {
    return message.user === '1';
  }

  private loadHistory(): void {
    this.chatService.getHistory(this.sessionId).subscribe({
      next: (response: FloatingChatHistoryResponse) => {
        this.messages = response.messages ?? [];
        if (!this.name.trim() && response.userName) {
          this.name = response.userName;
        }
        if (!this.tel.trim() && response.tel) {
          this.tel = response.tel;
        }
        this.persistIdentity();
        this.pendingScroll = this.messages.length > 0;
        this.requestRender();
      },
      error: () => {
        this.requestRender();
      }
    });
  }

  private persistIdentity(): void {
    this.storage.setItem(this.nameStorageKey, this.name.trim());
    this.storage.setItem(this.telStorageKey, this.tel.trim());
  }

  private ensureSessionId(): string {
    const existing = this.storage.getItem(this.sessionStorageKey);
    if (existing) {
      return existing;
    }
    const sessionId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    this.storage.setItem(this.sessionStorageKey, sessionId);
    return sessionId;
  }

  private startShakeInterval(): void {
    const triggerShake = () => {
      if (this.isVisible) {
        return;
      }
      this.isShaking = true;
      this.requestRender();
      setTimeout(() => {
        this.isShaking = false;
        this.requestRender();
      }, 700);
    };

    this.shakeTimeout = setTimeout(() => triggerShake(), 2500);
    this.shakeInterval = setInterval(() => triggerShake(), 8000);

    this.destroyRef.onDestroy(() => {
      clearTimeout(this.shakeTimeout);
      clearInterval(this.shakeInterval);
    });
  }

  /**
   * In a page header (`.nav-chat`) the open panel is fixed to the viewport just below the
   * header: the nearest fixed or sticky ancestor (admin `.top-navbar`, public `.public-top-bar`).
   * While the panel is open the header is watched, because its height changes without a window
   * resize (the public nav's hamburger menu). Nothing is watched while the chat is closed.
   */
  private trackPanelTop(): void {
    const host = this.host.nativeElement;
    if (!host.classList.contains('nav-chat')) {
      return;
    }
    let header: HTMLElement | null = host.parentElement;
    while (header && header !== document.body && !/^(fixed|sticky)$/.test(getComputedStyle(header).position)) {
      header = header.parentElement;
    }
    const anchor = header && header !== document.body ? header : host;
    const update = () => host.style.setProperty('--chat-panel-top', `${Math.round(anchor.getBoundingClientRect().bottom) + 8}px`);
    update();
    this.stopTrackingPanelTop();
    if (anchor !== host && typeof ResizeObserver !== 'undefined') {
      this.headerObserver = new ResizeObserver(update);
      this.headerObserver.observe(anchor);
    }
  }

  private stopTrackingPanelTop(): void {
    this.headerObserver?.disconnect();
    this.headerObserver = undefined;
  }

  private focusDraft(): void {
    queueMicrotask(() => this.draftInput?.nativeElement.focus());
  }

  private requestRender(): void {
    this.cdr.markForCheck();
  }
}
